/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module MessageItemsModule
 */

import { NgModule } from '@angular/core';

import {
    createNgDependencyProviders,
} from 'ng/modules/data-model/utils/inject-ng-dependencies.utils';

import {
    AlertSyslogServerConfigItem,
    AnalyticsPolicyConfigItem,
    AZClusterConfigItem,
    AZDatastoreConfigItem,
    AZHostConfigItem,
    AZVSphereZoneConfigItem,
    ClientInsightsSamplingConfigItem,
    ClientLogFilterConfigItem,
    ClusterNodeConfigItem,
    ContentRewriteProfileConfigItem,
    ControllerInterfaceConfigItem,
    CSRFRuleConfigItem,
    DNSConfigConfigItem,
    FailActionConfigItem,
    FailActionHttpLocalResponseConfigItem,
    FailActionHttpRedirectConfigItem,
    GslbClientIpAddrGroupConfigItem,
    GslbGeoLocationConfigItem,
    GslbIpAddrConfigItem,
    GslbPoolConfigItem,
    GslbPoolMemberConfigItem,
    GslbServiceDownResponseConfigItem,
    GslbSiteConfigItem,
    GslbSiteDnsVsConfigItem,
    GslbThirdPartySiteConfigItem,
    HTTPRedirectActionConfigItem,
    HTTPReselectRespCodeConfigItem,
    HTTPServerReselectConfigItem,
    HTTPStatusRangeConfigItem,
    LeaderChangeInfoConfigItem,
    PlacementNetworkConfigItem,
    PoolAnalyticsPolicyConfigItem,
    PoolGroupMemberConfigItem,
    RateLimiterActionConfigItem,
    ReqContentRewriteRuleConfigItem,
    RspContentRewriteRuleConfigItem,
    SearchReplacePairConfigItem,
    ServerConfigItem,
    StaticRouteConfigItem,
    TenantConfigurationConfigItem,
    TrustedHostConfigItem,
    VlanInterfaceConfigItem,
    VNICConfigItem,
    VNICNetworksConfigItem,
    WafConfigConfigItem,
    WafMatchElementSubParserConfigItem,
} from './factories';

const providers = createNgDependencyProviders([
    [CSRFRuleConfigItem, 'CSRFRuleConfigItem'],
    [FailActionConfigItem, 'FailActionConfigItem'],
    [FailActionHttpLocalResponseConfigItem, 'FailActionHTTPLocalResponseConfigItem'],
    [FailActionHttpRedirectConfigItem, 'FailActionHTTPRedirectConfigItem'],
    [HTTPRedirectActionConfigItem, 'HTTPRedirectActionConfigItem'],
    [HTTPReselectRespCodeConfigItem, 'HTTPReselectRespCodeConfigItem'],
    [HTTPServerReselectConfigItem, 'HTTPServerReselectConfigItem'],
    [HTTPStatusRangeConfigItem, 'HTTPStatusRangeConfigItem'],
    [PlacementNetworkConfigItem, 'PlacementNetworkConfigItem'],
    [ServerConfigItem, 'ServerConfigItem'],
    [PoolGroupMemberConfigItem, 'PoolGroupMemberConfigItem'],
    [VNICConfigItem, 'vNICConfigItem'],
    [VNICNetworksConfigItem, 'VNICNetworksConfigItem'],
    [VlanInterfaceConfigItem, 'VlanInterfaceConfigItem'],
    [ClusterNodeConfigItem, 'ClusterNodeConfigItem'],
    [StaticRouteConfigItem, 'StaticRouteConfigItem'],
    [ControllerInterfaceConfigItem, 'ControllerInterfaceConfigItem'],
    [GslbThirdPartySiteConfigItem, 'GslbThirdPartySiteConfigItem'],
    [GslbSiteConfigItem, 'GslbSiteConfigItem'],
    [GslbIpAddrConfigItem, 'GslbIpAddrConfigItem'],
    [GslbPoolConfigItem, 'GslbPoolConfigItem'],
    [GslbPoolMemberConfigItem, 'GslbPoolMemberConfigItem'],
    [GslbGeoLocationConfigItem, 'GslbGeoLocationConfigItem'],
    [GslbClientIpAddrGroupConfigItem, 'GslbClientIpAddrGroupConfigItem'],
    [GslbSiteDnsVsConfigItem, 'GslbSiteDnsVsConfigItem'],
    [DNSConfigConfigItem, 'DNSConfigConfigItem'],
    [AlertSyslogServerConfigItem, 'AlertSyslogServerConfigItem'],
    [PoolAnalyticsPolicyConfigItem, 'PoolAnalyticsPolicyConfigItem'],
    [GslbServiceDownResponseConfigItem, 'GslbServiceDownResponseConfigItem'],
    [AnalyticsPolicyConfigItem, 'AnalyticsPolicyConfigItem'],
    [ClientLogFilterConfigItem, 'ClientLogFilterConfigItem'],
    [ClientInsightsSamplingConfigItem, 'ClientInsightsSamplingConfigItem'],
    [ContentRewriteProfileConfigItem, 'ContentRewriteProfileConfigItem'],
    [RateLimiterActionConfigItem, 'RateLimiterActionConfigItem'],
    [ReqContentRewriteRuleConfigItem, 'ReqContentRewriteRuleConfigItem'],
    [RspContentRewriteRuleConfigItem, 'RspContentRewriteRuleConfigItem'],
    [SearchReplacePairConfigItem, 'SearchReplacePairConfigItem'],
    [TrustedHostConfigItem, 'TrustedHostConfigItem'],
    [AZClusterConfigItem, 'AZClusterConfigItem'],
    [TenantConfigurationConfigItem, 'TenantConfigurationConfigItem'],
    [WafConfigConfigItem, 'WafConfigConfigItem'],
    [WafMatchElementSubParserConfigItem, 'WafMatchElementSubParserConfigItem'],
    [AZDatastoreConfigItem, 'AZDatastoreConfigItem'],
    [AZHostConfigItem, 'AZHostConfigItem'],
    [AZVSphereZoneConfigItem, 'VSphereZoneConfigItem'],
    [LeaderChangeInfoConfigItem, 'LeaderChangeInfoConfigItem'],
]);

/**
 * @description
 * Module for registering MessageItem files. MessageItems can be imported by the 'message-items/*'
 * path which points to 'message-items/factories'. This module should contain providers only.
 *
 * MessageItems need to be registered with a string token, ex. 'ServerConfigItem' for the
 * ServerConfigItem class constructor. This is because MessageMapService injects messageItems by
 * string injection, where the string is dynamically generated based on the protobuf objectType.
 *
 * Note: Make sure that the string token matches the message (including casing) as defined in the
 * protobuf. For example, the class FailActionHttpRedirectConfigItem has camelcasing for 'Http' but
 * the message is 'FailActionHTTPRedirectConfigItem' with 'HTTP'.
 * @author alextsg, Umesh Chovatiya.
 */
@NgModule({
    providers,
})
export class MessageItemsModule {}
